// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getIsCheckoutPage } from "@eisinc/App/Store/Selectors/CheckoutSelector";
import { mergeWithCss, xClassNames } from "@eisinc/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";

export interface HeaderClickableItemProps {
    isOpen?: boolean;
    isHorizontal?: boolean;
    additionalElement?: React.ReactNode;
    extendedStyles?: HeaderClickableStyles;
}

export type HeaderClickableProps = HeaderClickableItemProps & ClickableProps;

type Props = HeaderClickableProps;

export interface HeaderClickableStyles {
    clickable?: ClickablePresentationProps;
    iconWrapper?: InjectableCss;
    icon?: IconPresentationProps;
    title?: TypographyPresentationProps;
    arrowIcon?: IconPresentationProps;
}

export const headerClickableStyles: HeaderClickableStyles = {};

const HeaderClickable: FC<Props> = ({
    isOpen,
    isHorizontal,
    additionalElement,
    children,
    extendedStyles,
    ...otherProps
}) => {
    const { isInShell } = useShellContext();
    const [styles] = useState(() => mergeWithCss(headerClickableStyles, extendedStyles));
    const className = xClassNames.append([isOpen && "opened"], otherProps.className);
    const isCheckoutPage = useAppSelector(state => getIsCheckoutPage(state));

    return (
        <Clickable {...styles.clickable} {...otherProps} className={className} {...{ isHorizontal, isInShell }}>
            {styles.icon?.src && (
                <StyledWrapper {...styles.iconWrapper}>
                    <Icon {...styles.icon} color={styles.icon.color} {...{ isInShell }} />
                    {additionalElement}
                </StyledWrapper>
            )}
            <Typography {...styles.title} color={styles.title?.color} {...{ isHorizontal, isInShell, isCheckoutPage }}>
                {children}
                {isHorizontal && <Icon {...styles.arrowIcon} className={className} />}
            </Typography>
        </Clickable>
    );
};

export default HeaderClickable;
